/**
 * PaymentsTab - Admin view for pilot payments
 * Shows all payment attempts with filtering and search
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { 
  CreditCard, Search, RefreshCw, CheckCircle2, XCircle, 
  Clock, Shield, ChevronDown, ChevronUp, Store
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { API_URL } from '../../../utils/apiConfig';

export function PaymentsTab({ token }) {
  const { t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPayment, setExpandedPayment] = useState(null);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '50');

      const response = await fetch(`${API_URL}/api/admin/payments?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    const icons = {
      paid: <CheckCircle2 className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />,
      pending: <Clock className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-800'}`}>
        {icons[status]}
        {status?.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <CreditCard className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Pilot Payments</h2>
            <p className="text-sm text-slate-500">View and manage payment attempts</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPayments} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-slate-500">Total Payments</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-slate-500">Successful</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.paid || 0}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-slate-500">Failed</p>
            <p className="text-2xl font-bold text-red-600">{stats.failed || 0}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-slate-500">Total Volume</p>
            <p className="text-2xl font-bold text-blue-600">${(stats.total_volume || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by payment ID, merchant, or payer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Payment ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Merchant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Loading payments...
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    No payments found
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <>
                    <tr key={payment.payment_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-slate-700">{payment.payment_id}</span>
                        {payment.is_simulated && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                            <Shield className="w-2.5 h-2.5" />
                            SIM
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{payment.merchant_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold">${payment.amount?.toFixed(2)}</span>
                        <span className="text-slate-400 ml-1">{payment.currency}</span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(payment.status)}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{payment.provider}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{formatDate(payment.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedPayment(
                            expandedPayment === payment.payment_id ? null : payment.payment_id
                          )}
                        >
                          {expandedPayment === payment.payment_id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                    {expandedPayment === payment.payment_id && (
                      <tr className="bg-slate-50">
                        <td colSpan="7" className="px-4 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Merchant ID:</span>
                              <p className="font-mono text-xs">{payment.merchant_id}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Payer Name:</span>
                              <p>{payment.payer_name || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Payer Phone:</span>
                              <p>{payment.payer_phone || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Memo:</span>
                              <p>{payment.memo || '-'}</p>
                            </div>
                            {payment.error_reason && (
                              <div className="col-span-2 md:col-span-4">
                                <span className="text-slate-500">Error:</span>
                                <p className="text-red-600">{payment.error_reason}</p>
                              </div>
                            )}
                            <div>
                              <span className="text-slate-500">Code Used:</span>
                              <p className="font-mono text-xs">{payment.code}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Location:</span>
                              <p>{payment.merchant_location || '-'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
